'use client';

import { useEffect, useRef, useState } from 'react';

import {
    Align,
    FlexBox,
    FlexItem,
    Icon,
    IconColor,
    IconName,
    IconSize,
    Title,
    TitleLevels,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';

import { Big } from '@components/big';

import { MobileCard } from './mobile-card';

type MobilePlansProps = {
    plans: MobilePlanType[];
    planToShow: number;
    onPlanChanges: (plans: MobilePlanType[]) => void;
};

export function MobilePlans({ plans, planToShow, onPlanChanges }: Readonly<MobilePlansProps>) {
    const selectedMobilePlans = useRef<MobilePlanType[]>([]);
    const lastIndexUpdated = useRef<number>(0);
    const initialPlanToShow = useRef<number>(planToShow);
    const [planNumber, setPlanNumber] = useState(planToShow);

    const onSelectedMobilePlan = async (plan: MobilePlanType, index: number) => {
        if (selectedMobilePlans?.current[index]?.gencode !== plan.gencode) {
            const newPlans = [...selectedMobilePlans.current];
            newPlans[index] = plan;
            selectedMobilePlans.current = newPlans;
            lastIndexUpdated.current = index;
            onPlanChanges(selectedMobilePlans.current);
        }
    };

    const refreshPlans = () => {
        if (planNumber < selectedMobilePlans.current.length) {
            selectedMobilePlans.current = selectedMobilePlans.current.slice(0, planNumber);
        } else {
            const newPlans: MobilePlanType[] = [...selectedMobilePlans.current];
            const defaultPlan = plans.find((p) => p.isDefault) || plans[0];
            for (let i = 0; i < planNumber; i++) {
                if (!newPlans[i] && defaultPlan) {
                    newPlans[i] = defaultPlan;
                }
            }
            selectedMobilePlans.current = newPlans;
            lastIndexUpdated.current = newPlans.length - 1;
        }
        onPlanChanges(selectedMobilePlans.current);
    };

    useEffect(() => {
        if (planToShow !== initialPlanToShow.current) {
            initialPlanToShow.current = planToShow;
            setPlanNumber(planToShow);
            refreshPlans();
        } else if (planNumber) {
            refreshPlans();
        }
    }, [planToShow, planNumber]);

    return (
        <FlexBox data-cy='mobile-list' wrap>
            {Array.from({ length: planNumber }, (_, i) => i).map((index) => {
                return (
                    <FlexItem key={`plan-${index}`} size={{ mobile: 12, tablet: 6 }} data-cy={`mobile-plan_${index}`}>
                        <MobileCard
                            plans={plans}
                            index={index}
                            currentSelected={selectedMobilePlans.current[index]}
                            onSelectedPlan={(plan) => onSelectedMobilePlan(plan, index)}
                            onRemovePlan={() => setPlanNumber(planNumber - 1)}
                            fullHeight={planNumber > 1}
                        />
                    </FlexItem>
                );
            })}
            {planNumber >= 4 && planNumber < 10 ? (
                <FlexItem
                    data-cy='add-more-plan-btn'
                    size={{ mobile: 12, tablet: 6 }}
                    className='has-background-info-fade'
                >
                    <View
                        data-cy='add-more-plan-btn'
                        flexable
                        align={Align.CENTER}
                        className='border-dashed-tertiary rounded-lg more-plan-container has-text-centered is-fullheight cursor-pointer is-flex-col gap-medium is-justified-center'
                        onClick={() => setPlanNumber(planNumber + 1)}
                    >
                        <Icon size={IconSize.HUGE} name={IconName.PLUS} color={IconColor.INFO} />
                        <Title
                            level={TitleLevels.FIVE}
                            typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_INFO]}
                        >
                            Ajouter un autre forfait = 10€/mois de <Big /> économies
                        </Title>
                    </View>
                </FlexItem>
            ) : null}
        </FlexBox>
    );
}
